import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBasicInfo, postBasicInfo } from "../../store/supplierFormDataSlice";
import ContinueButton from "../../Registration/ContinueButton";


const BasicInformation = ({ onNext, onSkip }) => {
  const dispatch = useDispatch();
  const basicInfo = useSelector((state) => state.formData.basicInfo || {});
  const userId = useSelector((state) => state.auth.id);
  const [showLegalNameConfirm, setShowLegalNameConfirm] = useState(false);
  const [isLegalNameConfirmed, setIsLegalNameConfirmed] = useState(
    !!basicInfo.legalName
  );

  const initialFormData = {
    name: basicInfo.name || "",
    legalName: basicInfo.legalName || "",
    dba: basicInfo.dba || "",
    description: basicInfo.description || "",
    businessPhone: basicInfo.businessPhone || "",
    website: basicInfo.website || "",
    foundedYear: basicInfo.foundedYear || "",
    createdBy: userId,
    accountType: "SUPPLIER",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [charCount, setCharCount] = useState(
    initialFormData.description.length
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const charLimit = 250;

  const validateForm = (data) => {
    const requiredFields = ["name", "description", "foundedYear"];
    return requiredFields.every((field) => data[field]?.trim?.());
  };

  const [isValid, setIsValid] = useState(validateForm(initialFormData));

  const hasFormChanged = () => {
    return Object.keys(initialFormData).some((key) => {
      if (key === "createdBy" || key === "accountType") return false;
      return formData[key] !== initialFormData[key];
    });
  };

  useEffect(() => {
    setIsValid(validateForm(formData));
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
  
    const isLegalNameField = name === "legalName";
    const legalNameLocked = !!basicInfo.legalName || isLegalNameConfirmed;
  
    setFormData((prev) => {
      const updatedData = { ...prev, [name]: value };
  
      if (name === "foundedYear" && value) {
        const currentYear = new Date().getFullYear();
        const yearValue = parseInt(value, 10);
        if (!isNaN(yearValue)) {
          updatedData.years_in_business = currentYear - yearValue;
        }
      }
  
      if (
        isLegalNameField &&
        !legalNameLocked &&
        value.trim()
      ) {
        setShowLegalNameConfirm(true);
      }
  
      return updatedData;
    });
  
    if (name === "description") {
      setCharCount(value.length);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    onNext();

    try {
      const formChanged = hasFormChanged();

      if (formChanged) {
        const submitData = {
          name: formData.name,
          legalName: formData.legalName,
          dba: formData.dba,
          description: formData.description,
          businessPhone: formData.businessPhone,
          website: formData.website,
          foundedYear: formData.foundedYear,
          createdBy: userId,
          accountType: "SUPPLIER",
        };

        const response = await dispatch(postBasicInfo(submitData)).unwrap();

        dispatch(setBasicInfo(submitData));
        if (!response.supplier?._id) {
          throw new Error("No supplier ID received from the server");
        }
      }

    } catch (error) {
      console.error("Failed to submit basic info:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="flex flex-col min-h-screen justify-start items-center py-10 px-6">
      <header className="mb-14">
        <h1 className="text-2xl font-semibold text-[#1D2639] text-center">
          Supplier Info
        </h1>
        <p className="text-base font-medium text-[#7E7E7E] text-center w-full sm:w-[572px] mt-2">
          Start by providing key details about your business. This foundational
          information helps buyers quickly understand who you are, making it
          easier to connect with the right opportunities.
        </p>
      </header>
      <form
        className="flex flex-col w-full sm:w-[572px]"
        onSubmit={handleSubmit}
      >
        {[
          { label: "Company Name", name: "name", required: true },
          { label: "Business Legal Name", name: "legalName", required: true },
          { label: "DBA Name", name: "dba" },
          {
            label: "Business Description",
            name: "description",
            type: "textarea",
            required: true,
          },
          {
            label: "Year Registered",
            name: "foundedYear",
            type: "number",
            required: true,
          },
          { label: "Business Phone", name: "businessPhone", type: "tel" },
          { label: "Website URL", name: "website", type: "url" },
        ].map(({ label, name, type = "text", required }) => {
          const isLegalNameField = name === "legalName";
          const legalNameLocked = !!basicInfo.legalName || isLegalNameConfirmed;


          return (
            <fieldset key={name} className="mb-8">
              <label
                className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
                htmlFor={name}
              >
                {label} {required && <span className="text-red-500">*</span>}
              </label>
              {type === "textarea" ? (
                <>
                  <textarea
                    id={name}
                    name={name}
                    className="w-full h-24 p-3 bg-[#F9F9F9] rounded-lg border-2 border-gray-300"
                    value={formData[name]}
                    onChange={handleChange}
                    maxLength={charLimit}
                    required={required}
                    disabled={isSubmitting}
                  />
                  <div className="text-right text-sm text-[#7E7E7E]">
                    {charCount}/{charLimit} characters
                  </div>
                </>
              ) : (
                <input
                  type={type}
                  id={name}
                  name={name}
                  placeholder={`Enter ${label}`}
                  className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg border-2 border-gray-300"
                  value={formData[name]}
                  onChange={(e) => {
                    if (
                      isLegalNameField &&
                      !isLegalNameConfirmed &&
                      e.target.value.trim()
                    ) {
                      setShowLegalNameConfirm(true);
                    }
                    handleChange(e);
                  }}
                  required={required}
                  disabled={name === "legalName" && legalNameLocked}


                />
              )}
            </fieldset>
          );
        })}

        <input
          type="hidden"
          name="years_in_business"
          value={formData.years_in_business}
        />

        <ContinueButton
          onClick={handleSubmit}
          label={isSubmitting ? "Saving..." : "Save and Continue"}
          isDisabled={!isValid || isSubmitting || !formData.legalName}
        />
      </form>

      {showLegalNameConfirm && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-[572px] w-full shadow-lg text-center">
            <h2 className="text-2xl font-semibold text-[#1D2739] mb-2">
              Are you sure this is your business?
            </h2>
            <p className="text-base font-medium text-[#7E7E7E] mb-10">
              Once you confirm your Business Legal Name, you won’t be able to
              change it in the future. Make sure that this is your company
              before moving on.
            </p>
            <div className="flex flex-col justify-center items-center gap-3 mt-6">
              <button
             onClick={() => {
              setIsLegalNameConfirmed(true);
              setShowLegalNameConfirm(false);
            }}
            
                className="flex w-[432px] h-[56px] p-5 justify-center items-center gap-2 rounded-[13px] bg-[#EAF524]"
              >
                Confirm
              </button>
              <button
                onClick={() => {
                  setFormData((prev) => ({ ...prev, legalName: "" }));
                  setShowLegalNameConfirm(false);
                }}
                className="text-[#7E7E7E] flex w-[432px] h-[56px] p-5 justify-center items-center gap-2 rounded-[13px] border"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BasicInformation;
